/** 頁面專用圖片快取：相同完整網址只讀取一次，包含進行中及失敗結果。 */
export class TemplateLogoCache {
  private entries = new Map<string, Promise<string>>();
  private objects = new Set<string>();
  private controllers = new Set<AbortController>();
  private queue: Array<() => void> = [];
  private active = 0;
  private disposed = false;
  private owners = 0;

  constructor(private fallback: string, private concurrency = 4) {}

  // 延後釋放以相容 React StrictMode 的 effect cleanup/setup，不發出第二次請求。
  retain() { this.owners++; }
  release() {
    this.owners--;
    Promise.resolve().then(() => { if (this.owners === 0) this.dispose(); });
  }

  load(url: string): Promise<string> {
    if (!url || this.disposed) return Promise.resolve(this.fallback);
    const existing = this.entries.get(url);
    if (existing) return existing;
    const result = new Promise<string>((resolve) => {
      this.queue.push(() => {
        if (this.disposed) { resolve(this.fallback); return; }
        this.active++;
        const controller = new AbortController();
        this.controllers.add(controller);
        // 圖片已有公開或簽章網址，不附帶會員 token，也不持久化私有圖片。
        Promise.resolve().then(() => fetch(url, {
          signal: controller.signal, credentials: 'omit', referrerPolicy: 'no-referrer',
        })).then(async (response) => {
          if (!response.ok) throw new Error('圖片載入失敗');
          const blob = await response.blob();
          if (!blob.type.startsWith('image/')) throw new Error('圖片格式無效');
          if (this.disposed) return this.fallback;
          const objectUrl = URL.createObjectURL(blob);
          this.objects.add(objectUrl);
          return objectUrl;
        }).then(resolve, () => resolve(this.fallback)).finally(() => {
          this.controllers.delete(controller);
          this.active--;
          this.pump();
        });
      });
    });
    this.entries.set(url, result);
    this.pump();
    return result;
  }

  private pump() {
    while (this.queue.length && (this.disposed || this.active < this.concurrency)) this.queue.shift()!();
  }

  dispose() {
    this.disposed = true;
    this.controllers.forEach(controller => controller.abort());
    this.objects.forEach(url => URL.revokeObjectURL(url));
    this.objects.clear();
    this.entries.clear();
    this.pump();
  }
}
